import React, { useState, useEffect, useRef } from "react";
import { Card, CardBody, Row, Col, Input, Label, FormGroup, Button } from "reactstrap";
import { StepComponentProps } from "../types/proposalForm.types";

// Keys match API: nominee_name, relationship, nominee_dob, passport_number, nominee_identity_no, country_code, nominee_local_contactno, nominee_mobileno, appointee_name, realtion_with_nominee, appointee_passportno, appointee_identityno, appointee_countycode, appintee_identity_expiry, appintee_digital_sign
interface NomineeData {
  nominee_name: string;
  relationship: string;
  nominee_dob: string;
  nominee_nationality: string;
  passport_number: string;
  passport_expiry: string;
  nominee_identity_no: string;
  nominee_identity_expiry: string;
  country_code: string;
  nominee_local_contactno: string;
  nominee_mobileno: string;
  percentage_share: string; // UI only, not in API
  nominee_address: string;
  is_minor: "Yes" | "No" | "";
  appointee_name: string;
  realtion_with_nominee: string;
  appointee_dob: string;
  appointee_nationality: string;
  appointee_passportno: string;
  appointee_passport_expiry: string;
  appointee_identityno: string;
  appintee_identity_expiry: string;
  appointee_countycode: string;
  appointee_mobileno: string;
  appointee_address: string;
  appintee_digital_sign: string;
}

const Step6Nominee: React.FC<StepComponentProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  setFieldValue,
}) => {
  const [nominees, setNominees] = useState<NomineeData[]>([
    {
      nominee_name: "",
      relationship: "",
      nominee_dob: "",
      nominee_nationality: "",
      passport_number: "",
      passport_expiry: "",
      nominee_identity_no: "",
      nominee_identity_expiry: "",
      country_code: "+973",
      nominee_local_contactno: "",
      nominee_mobileno: "",
      percentage_share: "",
      nominee_address: "",
      is_minor: "",
      appointee_name: "",
      realtion_with_nominee: "",
      appointee_dob: "",
      appointee_nationality: "",
      appointee_passportno: "",
      appointee_passport_expiry: "",
      appointee_identityno: "",
      appintee_identity_expiry: "",
      appointee_countycode: "+973",
      appointee_mobileno: "",
      appointee_address: "",
      appintee_digital_sign: "",
    },
  ]);

  const hasInitializedFromFormikRef = useRef(false);

  // When form is prefilled from API (nominee_details → nomineeDetails), initialize local state so fields display
  useEffect(() => {
    if (hasInitializedFromFormikRef.current) return;
    const list = values.nomineeDetails;
    if (!Array.isArray(list) || list.length === 0) return;
    const first = list[0];
    if (!first || String(first?.nominee_name ?? "").trim() === "") return;
    const toStr = (v: unknown) => (v != null && v !== "" ? String(v) : "");
    const mapped: NomineeData[] = list.map((n: any) => ({
      nominee_name: toStr(n.nominee_name),
      relationship: toStr(n.relationship),
      nominee_dob: toStr(n.nominee_dob),
      nominee_nationality: toStr(n.nominee_nationality),
      passport_number: toStr(n.passport_number),
      passport_expiry: toStr(n.passport_expiry),
      nominee_identity_no: toStr(n.nominee_identity_no),
      nominee_identity_expiry: toStr(n.nominee_identity_expiry),
      country_code: toStr(n.country_code) || "+973",
      nominee_local_contactno: toStr(n.nominee_local_contactno),
      nominee_mobileno: toStr(n.nominee_mobileno),
      percentage_share: "",
      nominee_address: toStr(n.nominee_address),
      is_minor: n.is_minor === 1 ? "Yes" : "",
      appointee_name: toStr(n.appointee_name),
      realtion_with_nominee: toStr(n.realtion_with_nominee),
      appointee_dob: toStr(n.appointee_dob),
      appointee_nationality: toStr(n.appointee_nationality),
      appointee_passportno: toStr(n.appointee_passportno),
      appointee_passport_expiry: toStr(n.appointee_passport_expiry),
      appointee_identityno: toStr(n.appointee_identityno),
      appintee_identity_expiry: toStr(n.appintee_identity_expiry),
      appointee_countycode: toStr(n.appointee_countycode) || "+973",
      appointee_mobileno: toStr(n.appointee_mobileno),
      appointee_address: toStr(n.appointee_address),
      appintee_digital_sign: toStr(n.appintee_digital_sign),
    }));
    setNominees(mapped);
    hasInitializedFromFormikRef.current = true;
  }, [values.nomineeDetails]);

  const isProduction = process.env.NODE_ENV === 'production';
  const countryCode = isProduction ? '+973' : '+91';

  const updateNominee = (index: number, field: keyof NomineeData, value: any) => {
    const updated = [...nominees];
    updated[index] = { ...updated[index], [field]: value };
    setNominees(updated);
  };

  // Sync nominee form data to formik for payload (exact API shape: null for empty optional dates, is_minor 0|1)
  const toStr = (v: unknown) => (v != null && v !== "" ? String(v) : "");
  const toStrNull = (v: unknown): string | null => (v != null && v !== "" ? String(v) : null);
  useEffect(() => {
    const payload = nominees.map((n) => ({
      nominee_name: toStr(n.nominee_name),
      relationship: toStr(n.relationship),
      nominee_dob: toStr(n.nominee_dob),
      nominee_nationality: toStr(n.nominee_nationality),
      passport_number: toStr(n.passport_number),
      passport_expiry: toStr(n.passport_expiry),
      nominee_identity_no: toStr(n.nominee_identity_no),
      nominee_identity_expiry: toStrNull(n.nominee_identity_expiry),
      country_code: toStr(n.country_code) || "+973",
      nominee_local_contactno: toStr(n.nominee_local_contactno),
      nominee_mobileno: toStr(n.nominee_mobileno),
      nominee_address: toStr(n.nominee_address),
      is_minor: (n.is_minor === "Yes" ? 1 : 0) as 0 | 1,
      appointee_name: toStr(n.appointee_name),
      realtion_with_nominee: toStr(n.realtion_with_nominee),
      appointee_dob: toStrNull(n.appointee_dob),
      appointee_nationality: toStr(n.appointee_nationality),
      appointee_passportno: toStr(n.appointee_passportno),
      appointee_passport_expiry: toStrNull(n.appointee_passport_expiry),
      appointee_identityno: toStr(n.appointee_identityno),
      appintee_identity_expiry: toStrNull(n.appintee_identity_expiry),
      appointee_countycode: toStr(n.appointee_countycode),
      appointee_mobileno: toStr(n.appointee_mobileno),
      appointee_address: toStr(n.appointee_address),
      appintee_digital_sign: toStr(n.appintee_digital_sign),
    }));
    setFieldValue("nomineeDetails", payload);
  }, [nominees, setFieldValue]);

  const renderInput = (
    value: string,
    onChange: (value: string) => void,
    label: string,
    type: "text" | "number" | "date" | "email" | "tel" = "text",
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="form-control"
      />
    </FormGroup>
  );

  const renderSelect = (
    value: string,
    onChange: (value: string) => void,
    label: string,
    options: Array<{ value: string; label: string }>,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="form-control"
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Input>
    </FormGroup>
  );

  const renderTextarea = (
    value: string,
    onChange: (value: string) => void,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <Input
        type="textarea"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="form-control"
        rows={3}
      />
    </FormGroup>
  );

  const renderDateInput = ( 
    value: string,
    onChange: (value: string) => void,
    label: string,
    placeholder?: string,
    required: boolean = false
  ) => (
    <FormGroup className="form-group">
      <Label>
        {label}
        {required && <span className="text-danger">*</span>}
      </Label>
      <div className="date-input-wrapper">
        <Input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "DD/MM/YYYY"}
          className="form-control date-input-field"
          onFocus={(e) => {
            e.target.type = "date";
          }}
        />
        <i 
          className="ri-calendar-line calendar-icon"
          onClick={(e) => {
            const wrapper = (e.currentTarget as HTMLElement).closest(".date-input-wrapper");
            const input = wrapper?.querySelector("input.date-input-field") as HTMLInputElement | null;
            if (input) {
              input.type = "date";
              input.focus();
              if (typeof input.showPicker === "function") {
                input.showPicker();
              } else {
                input.click();
              }
            }
          }}
        />
      </div>
    </FormGroup>
  );

  const renderCountryCodeSelect = (
    value: string,
    onChange: (value: string) => void,
    label: string
  ) => {
    return (
      <FormGroup className="form-group">
        <Label>{label}</Label>
        <div className="country-code-wrapper">
          <Input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            readOnly={true}
            className="form-control country-code-display"
            style={{ maxWidth: '100px', width: '100%' }}
          />
        </div>
      </FormGroup>
    );
  };

  return (
    <>
      {nominees.map((nominee, index) => (
        <Card key={index} className="form-card shadow-sm border-0" style={{ marginBottom: "2rem" }}>
          <CardBody>
            <h5 className="form-title">Nominee Details</h5>
            <p className="form-subtitle">
              Designate a beneficiary who will receive the policy benefits
            </p>

            {/* About Nominee Info Box */}
            <div className="about-nominee-info">
              <h6 className="about-nominee-title">About Nominee</h6>
              <p className="about-nominee-text">
                A nominee is the person who will receive the policy proceeds in the event of your demise. You can nominate one or more beneficiaries and allocate shares accordingly.
              </p>
            </div>

            {/* Nominee Details Section */}
            <Row>
              <Col md={6}>
                {renderInput(
                  nominee.nominee_name,
                  (val) => updateNominee(index, "nominee_name", val),
                  "Nominee Full Name",
                  "text",
                  "Type here",
                  true
                )}
              </Col>
              <Col md={6}>
                {renderInput(
                  nominee.relationship,
                  (val) => updateNominee(index, "relationship", val),
                  "Relationship with Proposer",
                  "text",
                  "e.g. Spouse",
                  true
                )}
              </Col>
              <Col md={4}>
                {renderDateInput(
                  nominee.nominee_dob,
                  (val) => updateNominee(index, "nominee_dob", val),
                  "Nominee Date of Birth",
                  "DD/MM/YYYY",
                  true
                )}
              </Col>
              <Col md={4}>
                {renderInput(
                  nominee.nominee_nationality,
                  (val) => updateNominee(index, "nominee_nationality", val),
                  "Nominee Nationality",
                  "text",
                  "e.g. Bahrain",
                  true
                )}
              </Col>
              <Col md={4}>
                {renderInput(
                  nominee.passport_number,
                  (val) => updateNominee(index, "passport_number", val),
                  "Passport Number",
                  "text",
                  "XXX"
                )}
              </Col>
              <Col md={4}>
                {renderDateInput(
                  nominee.passport_expiry,
                  (val) => updateNominee(index, "passport_expiry", val),
                  "Date of Expiry",
                  "DD/MM/YYYY"
                )}
              </Col>
              <Col md={4}>
                {renderInput(
                  nominee.nominee_identity_no,
                  (val) => updateNominee(index, "nominee_identity_no", val),
                  "CPR/ Civil/ Citizen card No.",
                  "text",
                  "XXX"
                )}
              </Col>
              <Col md={4}>
                {renderDateInput(
                  nominee.nominee_identity_expiry,
                  (val) => updateNominee(index, "nominee_identity_expiry", val),
                  "Date of Expiry",
                  "DD/MM/YYYY"
                )}
              </Col>
              <Col md="auto" style={{ maxWidth: '120px' }}>
                {renderCountryCodeSelect(
                  nominee.country_code,
                  (val) => updateNominee(index, "country_code", val),
                  "Country code"
                )}
              </Col>
              <Col md={3}>
                {renderInput(
                  nominee.nominee_local_contactno,
                  (val) => updateNominee(index, "nominee_local_contactno", val),
                  "Nominee Local Contact Number",
                  "tel",
                  "123"
                )}
              </Col>
              <Col md={3}>
                {renderInput(
                  nominee.nominee_mobileno,
                  (val) => updateNominee(index, "nominee_mobileno", val),
                  "Nominee Mobile Number",
                  "tel",
                  "123"
                )}
              </Col>
              <Col md={3}>
                {renderInput(
                  nominee.percentage_share,
                  (val) => updateNominee(index, "percentage_share", val),
                  "Percentage share",
                  "text",
                  "e.g. 50",
                  true
                )}
              </Col>
              <Col md={12}>
                {renderTextarea(
                  nominee.nominee_address,
                  (val) => updateNominee(index, "nominee_address", val),
                  "Nominee Address",
                  "Type here",
                  true
                )}
              </Col>
            </Row>

            {/* Is the nominee a minor? Section */}
            <div className="minor-question-section" style={{ marginTop: "1.5rem" }}>
              <FormGroup className="form-group">
                <Label className="minor-question-label">Is the nominee a minor?</Label>
                <div className="risk-assessment-options">
                  {(["Yes", "No"] as const).map((option) => (
                    <div key={option} className="risk-assessment-option">
                      <input
                        type="radio"
                        id={`minor-${index}-${option}`}
                        name={`minor-${index}`}
                        value={option}
                        checked={nominee.is_minor === option}
                        onChange={(e) => updateNominee(index, "is_minor", e.target.value as "Yes" | "No")}
                        className="risk-assessment-radio"
                      />
                      <label htmlFor={`minor-${index}-${option}`} className="risk-assessment-label">
                        {option}
                      </label>
                    </div>
                  ))}
                </div>
              </FormGroup>
            </div>

            {/* Appointee Details Section - Only shown if minor is Yes */}
            {nominee.is_minor === "Yes" && (
              <div className="appointee-section" style={{ marginTop: "2rem", paddingTop: "1.5rem", borderTop: "1px solid #e9ecef" }}>
                <h6 className="appointee-section-title">
                  Appointee Details (Required for minor nominee)
                  <i className="ri-information-line ms-2" style={{ fontSize: "1rem", color: "#6c757d" }}></i>
                </h6>

                <Row>
                  <Col md={6}>
                    {renderInput(
                      nominee.appointee_name,
                      (val) => updateNominee(index, "appointee_name", val),
                      "Appointee Full Name",
                      "text",
                      "Type here",
                      true
                    )}
                  </Col>
                  <Col md={6}>
                    {renderInput(
                      nominee.realtion_with_nominee,
                      (val) => updateNominee(index, "realtion_with_nominee", val),
                      "Relationship with Nominee",
                      "text",
                      "e.g. Parent",
                      true
                    )}
                  </Col>
                  <Col md={4}>
                    {renderDateInput(
                      nominee.appointee_dob,
                      (val) => updateNominee(index, "appointee_dob", val),
                      "Appointee Date of Birth",
                      "DD/MM/YYYY",
                      true
                    )}
                  </Col>
                  <Col md={4}>
                    {renderInput(
                      nominee.appointee_nationality,
                      (val) => updateNominee(index, "appointee_nationality", val),
                      "Appointee Nationality",
                      "text",
                      "e.g. Bahrain",
                      true
                    )}
                  </Col>
                  <Col md={4}>
                    {renderInput(
                      nominee.appointee_passportno,
                      (val) => updateNominee(index, "appointee_passportno", val),
                      "Passport Number",
                      "text",
                      "XXX"
                    )}
                  </Col>
                  <Col md={4}>
                    {renderDateInput(
                      nominee.appointee_passport_expiry,
                      (val) => updateNominee(index, "appointee_passport_expiry", val),
                      "Date of Expiry",
                      "DD/MM/YYYY"
                    )}
                  </Col>
                  <Col md={4}>
                    {renderInput(
                      nominee.appointee_identityno,
                      (val) => updateNominee(index, "appointee_identityno", val),
                      "CPR/ Civil/ Citizen card No.",
                      "text",
                      "XXX"
                    )}
                  </Col>
                  <Col md={4}>
                    {renderDateInput(
                      nominee.appintee_identity_expiry,
                      (val) => updateNominee(index, "appintee_identity_expiry", val),
                      "Date of Expiry",
                      "DD/MM/YYYY"
                    )}
                  </Col>
                  <Col md="auto" style={{ maxWidth: '120px' }}>
                    {renderCountryCodeSelect(
                      nominee.appointee_countycode,
                      (val) => updateNominee(index, "appointee_countycode", val),
                      "Country code"
                    )}
                  </Col>
                  <Col md={3}>
                    {renderInput(
                      nominee.appointee_mobileno,
                      (val) => updateNominee(index, "appointee_mobileno", val),
                      "Appointee Mobile Number",
                      "tel",
                      "123"
                    )}
                  </Col>
                  <Col md={12}>
                    {renderTextarea(
                      nominee.appointee_address,
                      (val) => updateNominee(index, "appointee_address", val),
                      "Appointee Address",
                      "Type here",
                      true
                    )}
                  </Col>
                </Row>

              </div>
            )}
          </CardBody>
        </Card>
      ))}

    </>
  );
};

export default Step6Nominee;
